/* Stage 5 — Combined B11/B12: Workstage 6 specialist admin pages.

   Six specialist surfaces (§3 of the brief):
     PaymentCertificationPage      — 561-07
     GlobalProgrammePage           — 561-08 (WS6 view of Global Programme)
     MiniProgrammeDetailPage       — 561-09
     ConstructionDeliveryPage      — 561-10
     ContractAdminPage             — 561-11
     ReviewCompletionPage          — 561-12 (Package + WS6)

   All copy in British English.  Human labels only — no 561-xx numbers.
   Follows the accepted global admin shell + canonical Project Workspace
   navigation (§74).  Reuses the same Pill/ModuleCard idiom from
   B9B10Pages.jsx for visual consistency.
*/
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { usePageMeta } from "../../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---- Small primitives --------------------------------------------------

function Pill({ tone = "neutral", children, testid }) {
    const map = {
        neutral: "bg-[#F5F3EE] text-[#1D1D1B]",
        good: "bg-[#E4EFE4] text-[#345E34]",
        warn: "bg-[#FBEEDF] text-[#7A4A22]",
        bad:  "bg-[#F6DFDF] text-[#7A2222]",
        active: "bg-[#A8543F] text-white",
    };
    return (
        <span data-testid={testid}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${map[tone]}`}>
            {children}
        </span>);
}

function Card({ title, testid, children, actions }) {
    return (
        <section data-testid={testid}
                 className="border border-[#E5E2DC] rounded-lg bg-white p-4 shadow-sm">
            <header className="mb-3 flex items-center justify-between">
                <h2 className="font-manrope text-[15px] font-semibold text-[#1D1D1B]">
                    {title}
                </h2>
                {actions ? <div className="flex gap-2">{actions}</div> : null}
            </header>
            <div className="text-[13px] text-[#1D1D1B]">{children}</div>
        </section>);
}

function KV({ label, value, testid }) {
    return (
        <div data-testid={testid}>
            <div className="text-[10px] uppercase tracking-widest text-[#666]">{label}</div>
            <div className="text-[14px] font-medium">{value ?? "—"}</div>
        </div>);
}


/**
 * C-B Correction §7 — Contract-controlled notice issuance UI hook.
 *
 * Placed alongside Contract Record on the Contract Admin surface where
 * the authoritative Contract Record is already in-context. This is
 * evidence/delivery of an already-effected contractual action — the
 * button must be invoked WITH `template_code` corresponding to a
 * `class=contractual`+`mode=contract_controlled` template and the
 * `formal_reference` merge value carrying the underlying contractual
 * evidence. It does NOT create the contractual action.
 */
function IssueContractualNoticeButton({ projectId, contractRecordId, idx }) {
    const [open, setOpen] = React.useState(false);
    const [tCode, setTCode] = React.useState("");
    const [ref, setRef] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    if (!contractRecordId) return null;
    async function submit(ev) {
        ev.preventDefault();
        setBusy(true);
        try {
            const opId = `iss-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
            await axios.post(`${API}/admin/contract-controlled/issue`,
                { project_id: projectId, template_code: tCode,
                  contract_record_id: contractRecordId,
                  merge_values: { formal_reference: ref },
                  create_op_id: opId },
                { withCredentials: true });
            setOpen(false); setTCode(""); setRef("");
        } catch (e) {
            // Left as-is — backend surfaces domain errors; toast/error handling
            // is admin surface's responsibility outside this hook.
        } finally {
            setBusy(false);
        }
    }
    return (
        <>
            <button data-testid={`pc-issue-contract-notice-${idx}`}
                    className="text-xs px-2 py-1 border border-[#E5E2DC] rounded hover:bg-[#F5F3EE]"
                    onClick={() => setOpen(true)}>
                Issue contractual notice
            </button>
            {open && (
                <div data-testid={`pc-issue-notice-dialog-${idx}`}
                     className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center"
                     onClick={() => setOpen(false)}>
                    <form onClick={ev => ev.stopPropagation()}
                          onSubmit={submit}
                          className="bg-white rounded-lg p-6 w-[420px] max-w-full space-y-3">
                        <h3 className="font-semibold text-sm">Issue contractual notice</h3>
                        <p className="text-xs text-[#666]">
                            Only issue AFTER the underlying contractual action has been
                            effected. This creates evidence — not the action itself.
                        </p>
                        <label className="block text-xs">
                            Template code (contractual / contract-controlled)
                            <input value={tCode}
                                   onChange={e => setTCode(e.target.value)}
                                   className="mt-1 w-full border rounded px-2 py-1 text-sm"
                                   data-testid={`pc-issue-notice-template-${idx}`}
                                   required />
                        </label>
                        <label className="block text-xs">
                            Formal reference
                            <input value={ref}
                                   onChange={e => setRef(e.target.value)}
                                   className="mt-1 w-full border rounded px-2 py-1 text-sm"
                                   data-testid={`pc-issue-notice-ref-${idx}`}
                                   required />
                        </label>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setOpen(false)}
                                    className="text-xs px-3 py-1 rounded border">
                                Cancel
                            </button>
                            <button type="submit" disabled={busy}
                                    data-testid={`pc-issue-notice-submit-${idx}`}
                                    className="text-xs px-3 py-1 rounded bg-[#1D1D1B] text-white">
                                {busy ? "Issuing..." : "Issue"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </>
    );
}

function currency(n) {
    if (n === null || n === undefined) return "—";
    if (typeof n !== "number") return String(n);
    return "£" + n.toLocaleString("en-GB", { maximumFractionDigits: 2 });
}

// =====================================================================
// 561-07 — Payment / Certification
// =====================================================================

export function PaymentCertificationPage() {
    const { projectId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [clientSafe, setClientSafe] = useState(false);
    usePageMeta({ title: "Payment certification · WS6 | Studio G4 Admin" });

    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(
                `${API}/admin/projects/${projectId}/ws6/payment-certification`,
                { params: { client_safe: clientSafe }, withCredentials: true });
            setData(r.data);
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
    }, [projectId, clientSafe]);
    useEffect(() => { refresh(); }, [refresh]);

    if (err) return <div data-testid="pc-error" className="text-red-700 text-[13px]">{err}</div>;
    if (!data) return <div data-testid="pc-loading" className="text-[13px] text-[#666]">Loading Payment / Certification…</div>;

    return (
        <div className="flex flex-col gap-4" data-testid="payment-certification-page">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="font-manrope text-2xl font-semibold text-charcoal">Payment / Certification</h1>
                    <p className="text-[13px] text-[#666]">Applicant submitted position, CA assessment, and formal Payment Certificates — kept distinct. Certification is not payment.</p>
                </div>
                <label className="flex items-center gap-2 text-[12px]" data-testid="pc-toggle-client-safe">
                    <input type="checkbox" checked={clientSafe}
                           onChange={(e) => setClientSafe(e.target.checked)}
                           data-testid="pc-client-safe-checkbox" />
                    Client-facing view
                </label>
            </header>

            {(data.contracts || []).map((c, i) => {
                const cr = c.contract_record || {};
                const pos = c.commercial_position || {};
                return (
                    <div key={cr.id || i} className="flex flex-col gap-3"
                         data-testid={`pc-contract-block-${i}`}>
                        <Card title={cr.contract_title || `Contract Record ${cr.id?.slice(0, 8)}`}
                              testid={`pc-contract-card-${i}`}
                              actions={<IssueContractualNoticeButton
                                        projectId={projectId}
                                        contractRecordId={cr.id}
                                        idx={i} />}>
                            <div className="grid grid-cols-4 gap-4"
                                 data-testid={`pc-commercial-position-${i}`}>
                                <KV label="Original Sum" testid={`pc-original-${i}`}
                                    value={currency(pos.original_contract_sum)} />
                                <KV label="Effective Changes" testid={`pc-effective-changes-${i}`}
                                    value={currency(pos.effective_contract_changes_total)} />
                                <KV label="Adjusted Sum" testid={`pc-adjusted-${i}`}
                                    value={currency(pos.current_adjusted_contract_sum)} />
                                <KV label="Pending Changes" testid={`pc-pending-${i}`}
                                    value={currency(pos.pending_changes_total)} />
                                <KV label="Certified / Valued to Date" testid={`pc-certified-${i}`}
                                    value={currency(pos.certified_valued_to_date)} />
                                <KV label="Current Certificate" testid={`pc-current-cert-${i}`}
                                    value={currency(pos.current_certificate_amount)} />
                                <KV label="Remaining (contractual)" testid={`pc-remaining-${i}`}
                                    value={currency(pos.remaining_contract_value)} />
                                <KV label="Paid to Date" testid={`pc-paid-${i}`}
                                    value={pos.paid_to_date} />
                            </div>
                            <p className="text-[11px] text-[#666] mt-3">
                                {pos.labels?.remaining_contract_value}{" "}
                                {pos.labels?.certification_vs_payment}
                            </p>
                        </Card>

                        <Card title="Payment Applications" testid={`pc-applications-${i}`}>
                            {(c.applications || []).length === 0 && (
                                <p className="text-[12px] text-[#666]" data-testid={`pc-no-apps-${i}`}>
                                    No applications yet.
                                </p>)}
                            <ul className="flex flex-col gap-2">
                                {(c.applications || []).map((a) => (
                                    <li key={a.id} className="border border-[#E5E2DC] rounded p-2"
                                        data-testid={`pc-app-${a.id}`}>
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium">{a.applicant} — {a.application_reference || a.id.slice(0, 6)}</span>
                                            <Pill tone="neutral">{a.status}</Pill>
                                        </div>
                                        <div className="text-[11px] text-[#666] mt-1">
                                            Submitted {currency(a.submitted?.amount)} for {a.period_start} → {a.period_end}
                                        </div>
                                        {a.assessment && (
                                            <div className="text-[11px] text-[#7A4A22] mt-1"
                                                 data-testid={`pc-app-assessment-${a.id}`}>
                                                Assessment: {currency(a.assessment.assessed_amount)} ({a.assessment.assessment_state})
                                            </div>)}
                                    </li>))}
                            </ul>
                        </Card>

                        <Card title="Payment Certificates" testid={`pc-certificates-${i}`}>
                            {(c.certificates || []).length === 0 && (
                                <p className="text-[12px] text-[#666]" data-testid={`pc-no-certs-${i}`}>
                                    No certificates yet.
                                </p>)}
                            <ul className="flex flex-col gap-2">
                                {(c.certificates || []).map((cert) => (
                                    <li key={cert.id} className="border border-[#E5E2DC] rounded p-2"
                                        data-testid={`pc-cert-${cert.id}`}>
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium">{cert.certificate_reference || cert.id.slice(0, 6)}</span>
                                            <Pill tone={cert.status === "Issued" ? "good" : "warn"}>{cert.status}</Pill>
                                        </div>
                                        <div className="text-[11px] text-[#666]">
                                            Certified {currency(cert.certified?.amount)}
                                            {cert.service_method ? ` · via ${cert.service_method}` : null}
                                            {cert.issued_at ? ` · issued ${cert.issued_at.slice(0, 10)}` : null}
                                        </div>
                                    </li>))}
                            </ul>
                        </Card>
                    </div>);
            })}
        </div>);
}


// =====================================================================
// 561-08 — Global Programme (WS6 aggregate view)
// =====================================================================

export function GlobalProgrammePage() {
    const { projectId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [clientSafe, setClientSafe] = useState(false);
    const [linkedWorkstage, setLinkedWorkstage] = useState("");
    const [view, setView] = useState("list");   // "list" | "gantt"
    const [showAddItem, setShowAddItem] = useState(false);
    const [showAddMini, setShowAddMini] = useState(false);
    const [editing, setEditing] = useState(null);   // item obj
    usePageMeta({ title: "Global programme · WS6 | Studio G4 Admin" });
    const refresh = useCallback(async () => {
        try {
            const params = { client_safe: clientSafe };
            if (linkedWorkstage) params.linked_workstage = linkedWorkstage;
            const r = await axios.get(
                `${API}/admin/projects/${projectId}/ws6/programme`,
                { params, withCredentials: true });
            setData(r.data);
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
    }, [projectId, clientSafe, linkedWorkstage]);
    useEffect(() => { refresh(); }, [refresh]);
    if (err) return <div data-testid="prog-error" className="text-red-700 text-[13px]">{err}</div>;
    if (!data) return <div data-testid="prog-loading" className="text-[13px] text-[#666]">Loading Programme…</div>;

    const buckets = data.counts?.by_status || {};
    const sortedItems = [...(data.items || [])].sort((a, b) => {
        const da = a.planned_start_at || a.planned_end_at || "";
        const db = b.planned_start_at || b.planned_end_at || "";
        return da < db ? -1 : (da > db ? 1 : 0);
    });

    return (
        <div className="flex flex-col gap-4" data-testid="global-programme-page">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="font-manrope text-2xl font-semibold text-charcoal">Global Programme</h1>
                    <p className="text-[13px] text-[#666]">
                        Operational management surface over the ONE Global Programme (A5/B3).
                        Locked status vocabulary; no CPM, no dependencies, no automatic rescheduling, no nested Mini Programmes.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-[12px]">
                        <input type="checkbox" checked={clientSafe}
                               onChange={(e) => setClientSafe(e.target.checked)}
                               data-testid="prog-client-safe-checkbox" />
                        Client-facing view
                    </label>
                    <select value={linkedWorkstage}
                            onChange={(e) => setLinkedWorkstage(e.target.value)}
                            className="border border-[#E5E2DC] rounded px-2 py-1 text-[12px]"
                            data-testid="prog-filter-ws">
                        <option value="">All Workstages</option>
                        {[1,2,3,4,5,6,7].map((w) => <option key={w} value={w}>WS{w}</option>)}
                    </select>
                    <div className="inline-flex border border-[#E5E2DC] rounded overflow-hidden">
                        <button type="button" onClick={() => setView("list")}
                                className={`px-2 py-1 text-[12px] ${view === "list" ? "bg-[#F5F2ED]" : ""}`}
                                data-testid="prog-view-list">List</button>
                        <button type="button" onClick={() => setView("gantt")}
                                className={`px-2 py-1 text-[12px] ${view === "gantt" ? "bg-[#F5F2ED]" : ""}`}
                                data-testid="prog-view-gantt">Gantt</button>
                    </div>
                </div>
            </header>

            {!clientSafe && (
                <div className="flex gap-2" data-testid="prog-actions">
                    <button type="button" onClick={() => setShowAddItem(true)}
                            className="bg-[#A8543F] text-white text-[12px] px-3 py-1.5 rounded"
                            data-testid="prog-btn-add-item">+ Add Programme Item</button>
                    <button type="button" onClick={() => setShowAddMini(true)}
                            className="border border-[#A8543F] text-[#A8543F] text-[12px] px-3 py-1.5 rounded"
                            data-testid="prog-btn-add-mini">+ Add Mini Programme</button>
                </div>)}

            <Card title={`Programme summary (${data.counts?.total_items ?? 0} items)`}
                  testid="prog-summary">
                <div className="grid grid-cols-5 gap-3">
                    {Object.entries(buckets).map(([k, v]) => (
                        <KV key={k} label={k} value={v}
                            testid={`prog-count-${k.replace(/\s+/g, "-")}`} />))}
                </div>
            </Card>

            <Card title="Mini Programmes" testid="prog-mini-list">
                {(data.mini_programmes || []).length === 0 && (
                    <p className="text-[12px] text-[#666]" data-testid="prog-no-minis">No Mini Programmes.</p>)}
                <ul className="flex flex-col gap-2">
                    {(data.mini_programmes || []).map((m) => (
                        <li key={m.id} className="border border-[#E5E2DC] rounded p-2"
                            data-testid={`prog-mini-${m.id}`}>
                            <div className="flex items-center justify-between">
                                <Link to={`/admin/projects/${projectId}/ws6/mini-programmes/${m.id}`}
                                      className="font-medium hover:underline"
                                      data-testid={`prog-mini-link-${m.id}`}>
                                    {m.name || m.title || m.id.slice(0, 8)}
                                </Link>
                                <Pill tone="neutral">{m.date_mode || "derived"}</Pill>
                            </div>
                            <div className="text-[11px] text-[#666]">
                                Linked Workstage: {m.linked_workstage ?? "—"} · Children: {(m.child_ids || []).length}
                            </div>
                        </li>))}
                </ul>
            </Card>

            {view === "list" ? (
                <Card title="Programme items" testid="prog-items-list">
                    <ul className="flex flex-col gap-1">
                        {sortedItems.map((it) => (
                            <li key={it.id}
                                className="flex items-center justify-between border-b border-[#E5E2DC] py-1"
                                data-testid={`prog-item-${it.id}`}>
                                <div>
                                    <span className="font-medium">{it.title}</span>
                                    <span className="text-[11px] text-[#666] ml-2">
                                        WS{it.linked_workstage ?? "—"}
                                        {it.planned_start_at && ` · ${(it.planned_start_at || "").slice(0, 10)}`}
                                        {it.planned_end_at && ` → ${(it.planned_end_at || "").slice(0, 10)}`}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Pill tone={it.status === "Complete" ? "good"
                                               : it.status === "On Hold" ? "warn"
                                               : it.status === "Awaiting Client" ? "warn"
                                               : "neutral"}>
                                        {it.status || "Unstated"}
                                    </Pill>
                                    {!clientSafe && (
                                        <button type="button" onClick={() => setEditing(it)}
                                                className="text-[11px] underline text-[#A8543F]"
                                                data-testid={`prog-edit-${it.id}`}>Manage</button>)}
                                </div>
                            </li>))}
                    </ul>
                </Card>
            ) : (
                <Card title="Programme timeline" testid="prog-gantt">
                    <ProgrammeGantt items={sortedItems} />
                </Card>
            )}

            {showAddItem && (
                <AddProgrammeItemModal
                    projectId={projectId}
                    onClose={() => setShowAddItem(false)}
                    onCreated={() => { setShowAddItem(false); refresh(); }} />)}
            {showAddMini && (
                <AddMiniProgrammeModal
                    projectId={projectId}
                    onClose={() => setShowAddMini(false)}
                    onCreated={() => { setShowAddMini(false); refresh(); }} />)}
            {editing && (
                <ManageItemModal
                    projectId={projectId} item={editing}
                    onClose={() => setEditing(null)}
                    onChanged={() => { setEditing(null); refresh(); }} />)}
        </div>);
}


// ---- Programme sub-components (reuse A5/B3 endpoints — no new engine) ----

function ProgrammeGantt({ items }) {
    // Simple List-Gantt: horizontal date band per item.  Reuses the
    // programme dates as returned by A5 — no CPM / dependency engine.
    const withDates = items.filter((it) => it.planned_start_at && it.planned_end_at);
    if (withDates.length === 0) {
        return <p className="text-[12px] text-[#666]" data-testid="prog-gantt-empty">No items with planned dates.</p>;
    }
    const min = Math.min(...withDates.map((it) => new Date(it.planned_start_at).getTime()));
    const max = Math.max(...withDates.map((it) => new Date(it.planned_end_at).getTime()));
    const span = Math.max(1, max - min);
    return (
        <ul className="flex flex-col gap-1" data-testid="prog-gantt-list">
            {withDates.map((it) => {
                const s = new Date(it.planned_start_at).getTime();
                const e = new Date(it.planned_end_at).getTime();
                const left = ((s - min) / span) * 100;
                const width = Math.max(2, ((e - s) / span) * 100);
                return (
                    <li key={it.id} className="grid grid-cols-[220px_1fr] items-center gap-2 py-1"
                        data-testid={`prog-gantt-item-${it.id}`}>
                        <span className="text-[12px] truncate">{it.title}</span>
                        <div className="relative h-3 bg-[#F5F2ED] rounded">
                            <div className="absolute h-3 bg-[#A8543F] rounded"
                                 style={{ left: `${left}%`, width: `${width}%` }} />
                        </div>
                    </li>);
            })}
        </ul>);
}

function AddProgrammeItemModal({ projectId, onClose, onCreated }) {
    const [title, setTitle] = useState("");
    const [linkedWs, setLinkedWs] = useState(6);
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            await axios.post(`${API}/admin/projects/${projectId}/programme`, {
                title, linked_workstage: Number(linkedWs),
                planned_start_at: start || null, planned_end_at: end || null,
                create_op_id: `ui-item-${Date.now()}`,
            }, { withCredentials: true });
            onCreated();
        } catch (e2) { setErr(e2?.response?.data?.error || e2.message); }
        finally { setBusy(false); }
    };
    return (
        <Modal title="Add Programme Item" onClose={onClose} testid="prog-add-item-modal">
            <form onSubmit={submit} className="flex flex-col gap-2">
                <label className="text-[12px]">Title
                    <input required value={title} onChange={(e) => setTitle(e.target.value)}
                           className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                           data-testid="prog-add-item-title" />
                </label>
                <label className="text-[12px]">Linked Workstage
                    <select value={linkedWs} onChange={(e) => setLinkedWs(e.target.value)}
                            className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                            data-testid="prog-add-item-ws">
                        {[1,2,3,4,5,6,7].map((w) => <option key={w} value={w}>WS{w}</option>)}
                    </select>
                </label>
                <div className="grid grid-cols-2 gap-2">
                    <label className="text-[12px]">Planned start
                        <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                               className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                               data-testid="prog-add-item-start" />
                    </label>
                    <label className="text-[12px]">Planned end
                        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                               className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                               data-testid="prog-add-item-end" />
                    </label>
                </div>
                {err && <p className="text-[12px] text-red-700" data-testid="prog-add-item-err">{err}</p>}
                <div className="flex justify-end gap-2 mt-2">
                    <button type="button" onClick={onClose}
                            className="text-[12px] px-3 py-1.5">Cancel</button>
                    <button type="submit" disabled={busy}
                            className="bg-[#A8543F] text-white text-[12px] px-3 py-1.5 rounded"
                            data-testid="prog-add-item-submit">{busy ? "Adding…" : "Add Item"}</button>
                </div>
            </form>
        </Modal>);
}

function AddMiniProgrammeModal({ projectId, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [linkedWs, setLinkedWs] = useState(6);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            await axios.post(`${API}/admin/projects/${projectId}/mini_programmes`, {
                name, linked_workstage: Number(linkedWs),
                create_op_id: `ui-mini-${Date.now()}`,
            }, { withCredentials: true });
            onCreated();
        } catch (e2) { setErr(e2?.response?.data?.error || e2.message); }
        finally { setBusy(false); }
    };
    return (
        <Modal title="Add Mini Programme" onClose={onClose} testid="prog-add-mini-modal">
            <form onSubmit={submit} className="flex flex-col gap-2">
                <label className="text-[12px]">Name
                    <input required value={name} onChange={(e) => setName(e.target.value)}
                           className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                           data-testid="prog-add-mini-name" />
                </label>
                <label className="text-[12px]">Linked Workstage
                    <select value={linkedWs} onChange={(e) => setLinkedWs(e.target.value)}
                            className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                            data-testid="prog-add-mini-ws">
                        {[1,2,3,4,5,6,7].map((w) => <option key={w} value={w}>WS{w}</option>)}
                    </select>
                </label>
                <p className="text-[11px] text-[#666]">
                    Nested Mini Programmes are not permitted (A5 hierarchy invariant).
                </p>
                {err && <p className="text-[12px] text-red-700" data-testid="prog-add-mini-err">{err}</p>}
                <div className="flex justify-end gap-2 mt-2">
                    <button type="button" onClick={onClose}
                            className="text-[12px] px-3 py-1.5">Cancel</button>
                    <button type="submit" disabled={busy}
                            className="bg-[#A8543F] text-white text-[12px] px-3 py-1.5 rounded"
                            data-testid="prog-add-mini-submit">{busy ? "Adding…" : "Add Mini Programme"}</button>
                </div>
            </form>
        </Modal>);
}

function ManageItemModal({ projectId, item, onClose, onChanged }) {
    // Reuses the accepted A5/B3 operational routes:
    //   PATCH  /programme/{item_id}                — edit title/WS
    //   POST   /programme/{item_id}/reschedule     — planned dates
    //   POST   /programme/{item_id}/status         — managed status
    const [title, setTitle] = useState(item.title || "");
    const [ws, setWs] = useState(item.linked_workstage || 6);
    const [start, setStart] = useState((item.planned_start_at || "").slice(0, 10));
    const [end, setEnd] = useState((item.planned_end_at || "").slice(0, 10));
    const [status, setStatus] = useState(item.status || "Upcoming");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const save = async () => {
        setBusy(true); setErr("");
        try {
            // 1) title / linked_workstage
            if (title !== item.title || Number(ws) !== item.linked_workstage) {
                await axios.patch(`${API}/admin/programme/${item.id}`, {
                    title, linked_workstage: Number(ws),
                    expected_version: item.record_version,
                }, { withCredentials: true });
            }
            // 2) reschedule
            if (start !== (item.planned_start_at || "").slice(0, 10)
                || end !== (item.planned_end_at || "").slice(0, 10)) {
                await axios.post(`${API}/admin/programme/${item.id}/reschedule`, {
                    planned_start_at: start || null,
                    planned_end_at: end || null,
                    reason: "Admin adjustment",
                    op_id: `ui-resched-${Date.now()}`,
                }, { withCredentials: true });
            }
            // 3) status
            if (status !== item.status) {
                await axios.post(`${API}/admin/programme/${item.id}/status`, {
                    status, op_id: `ui-status-${Date.now()}`,
                }, { withCredentials: true });
            }
            onChanged();
        } catch (e2) { setErr(e2?.response?.data?.error || e2.message); }
        finally { setBusy(false); }
    };
    return (
        <Modal title={`Manage: ${item.title}`} onClose={onClose} testid="prog-manage-modal">
            <div className="flex flex-col gap-2">
                <label className="text-[12px]">Title
                    <input value={title} onChange={(e) => setTitle(e.target.value)}
                           className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                           data-testid="prog-manage-title" />
                </label>
                <label className="text-[12px]">Linked Workstage
                    <select value={ws} onChange={(e) => setWs(e.target.value)}
                            className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                            data-testid="prog-manage-ws">
                        {[1,2,3,4,5,6,7].map((w) => <option key={w} value={w}>WS{w}</option>)}
                    </select>
                </label>
                <div className="grid grid-cols-2 gap-2">
                    <label className="text-[12px]">Planned start
                        <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                               className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                               data-testid="prog-manage-start" />
                    </label>
                    <label className="text-[12px]">Planned end
                        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                               className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                               data-testid="prog-manage-end" />
                    </label>
                </div>
                <label className="text-[12px]">Managed status
                    <select value={status} onChange={(e) => setStatus(e.target.value)}
                            className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                            data-testid="prog-manage-status">
                        {["Upcoming","In Progress","Awaiting Client","On Hold","Complete"].map((s) =>
                            <option key={s} value={s}>{s}</option>)}
                    </select>
                </label>
                {err && <p className="text-[12px] text-red-700" data-testid="prog-manage-err">{err}</p>}
                <div className="flex justify-end gap-2 mt-2">
                    <button type="button" onClick={onClose}
                            className="text-[12px] px-3 py-1.5">Cancel</button>
                    <button type="button" disabled={busy} onClick={save}
                            className="bg-[#A8543F] text-white text-[12px] px-3 py-1.5 rounded"
                            data-testid="prog-manage-save">{busy ? "Saving…" : "Save"}</button>
                </div>
            </div>
        </Modal>);
}

function Modal({ title, onClose, testid, children }) {
    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
             data-testid={testid || "modal"}>
            <div className="bg-white rounded shadow-lg w-[min(560px,92vw)] max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between border-b border-[#E5E2DC] px-4 py-2">
                    <h2 className="font-manrope text-[15px] font-semibold">{title}</h2>
                    <button type="button" onClick={onClose}
                            className="text-[13px] text-[#666]"
                            data-testid={`${testid || "modal"}-close`}>×</button>
                </div>
                <div className="p-4">{children}</div>
            </div>
        </div>);
}


// =====================================================================
// 561-09 — Mini Programme Detail
// =====================================================================

export function MiniProgrammeWs6DetailPage() {
    const { projectId, mid } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const miniLabel = data?.mini_programme?.name || data?.mini_programme?.title || "Mini programme";
    usePageMeta({ title: `${miniLabel} · WS6 Mini programme | Studio G4 Admin` });
    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(
                    `${API}/admin/projects/${projectId}/ws6/mini-programmes/${mid}`,
                    { withCredentials: true });
                setData(r.data);
            } catch (e) { setErr(e?.response?.data?.error || e.message); }
        })();
    }, [projectId, mid]);
    if (err) return <div data-testid="mp-error" className="text-red-700 text-[13px]">{err}</div>;
    if (!data) return <div data-testid="mp-loading" className="text-[13px] text-[#666]">Loading Mini Programme…</div>;
    const m = data.mini_programme;
    return (
        <div className="flex flex-col gap-4" data-testid="mini-programme-detail-page">
            <header>
                <Link to={`/admin/projects/${projectId}/ws6/programme`}
                      className="text-[12px] text-[#A8543F] hover:underline"
                      data-testid="mp-back">← Back to Programme</Link>
                <h1 className="font-manrope text-2xl font-semibold text-charcoal mt-1">
                    {m.name || m.title || m.id.slice(0, 8)}
                </h1>
            </header>
            <Card title="Mini Programme details" testid="mp-details">
                <div className="grid grid-cols-4 gap-4">
                    <KV label="Linked Workstage" value={`WS${m.linked_workstage ?? "—"}`} testid="mp-ws" />
                    <KV label="Date Mode" value={data.date_mode} testid="mp-date-mode" />
                    <KV label="Children" value={data.child_count} testid="mp-children-count" />
                    <KV label="Client Visibility" value={m.client_visibility} testid="mp-vis" />
                </div>
                {data.fixed_dates && (
                    <div className="mt-3 text-[11px] text-[#7A4A22]"
                         data-testid="mp-fixed-dates">
                        Fixed Parent Dates active.  Child dates outside the fixed range raise Attention rather than silently extending the parent.
                    </div>)}
            </Card>
            <Card title="Children" testid="mp-children">
                <ul className="flex flex-col gap-1">
                    {(data.children || []).map((c) => (
                        <li key={c.id} className="border-b border-[#E5E2DC] py-1 flex justify-between"
                            data-testid={`mp-child-${c.id}`}>
                            <span>{c.title}</span>
                            <Pill tone="neutral">{c.status}</Pill>
                        </li>))}
                </ul>
            </Card>
        </div>);
}


// =====================================================================
// 561-10 — Construction / D&B
// =====================================================================

export function ConstructionDeliveryPage() {
    const { projectId, pkgId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const pkgLabel = data?.package?.package_name || "Package";
    usePageMeta({ title: `Construction · ${pkgLabel} | Studio G4 Admin` });
    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(
                    `${API}/admin/projects/${projectId}/ws6/construction/${pkgId}`,
                    { withCredentials: true });
                setData(r.data);
            } catch (e) { setErr(e?.response?.data?.error || e.message); }
        })();
    }, [projectId, pkgId]);
    if (err) return <div data-testid="con-error" className="text-red-700 text-[13px]">{err}</div>;
    if (!data) return <div data-testid="con-loading" className="text-[13px] text-[#666]">Loading Construction / Design & Build…</div>;
    const pkg = data.package;
    const dr = data.design_responsibility || {};
    const cr = data.governing_construction_contract || {};
    const mob = data.mobilisation_readiness || {};
    const shared = data.shared_records || {};
    const routing = data.routing || {};
    return (
        <div className="flex flex-col gap-4" data-testid="construction-delivery-page">
            <header>
                <h1 className="font-manrope text-2xl font-semibold text-charcoal">
                    Construction / Design &amp; Build — {pkg.package_name}
                </h1>
                <p className="text-[13px] text-[#666]">Specialist operational view for the appointed construction package.</p>
            </header>

            <Card title="Package status" testid="con-status">
                <div className="grid grid-cols-4 gap-4">
                    <KV label="Status" value={pkg.status} testid="con-pkg-status" />
                    <KV label="Design Responsibility" value={dr.value || "—"} testid="con-design-resp" />
                    <KV label="Contract Basis" value={cr.contract_basis} testid="con-contract-basis" />
                    <KV label="Mobilisation Ready" value={mob.ready ? "Yes" : "No"} testid="con-mob-ready" />
                </div>
                {dr.description ? (
                    <p className="text-[11px] text-[#666] mt-2" data-testid="con-design-resp-desc">
                        {dr.description}
                    </p>) : null}
            </Card>

            <Card title="Governing construction Contract Record" testid="con-contract">
                <div className="grid grid-cols-4 gap-4">
                    <KV label="Executed" value={cr.execution_state === "executed" ? "Yes" : "No"} testid="con-executed" />
                    <KV label="Effective" value={cr.effective_state === "effective" ? "Yes" : "No"} testid="con-effective" />
                    <KV label="Contract Sum" value={currency(cr.particulars?.contract_sum)} testid="con-sum" />
                </div>
            </Card>

            <Card title="Routing" testid="con-routing">
                <div className="flex flex-wrap gap-2 text-[12px]">
                    <Link to={routing.programme || "#"} className="underline text-[#A8543F]" data-testid="con-route-programme">Programme</Link>
                    <Link to={routing.payment_certification || "#"} className="underline text-[#A8543F]" data-testid="con-route-payment">Payment / Certification</Link>
                    <Link to={routing.contract_admin_actions || "#"} className="underline text-[#A8543F]" data-testid="con-route-ca">Contract Actions</Link>
                    <Link to={routing.review_completion || "#"} className="underline text-[#A8543F]" data-testid="con-route-completion">Review Completion</Link>
                </div>
            </Card>

            <Card title="Shared records (this package)" testid="con-shared">
                <div className="grid grid-cols-4 gap-4">
                    <KV label="Programme Items" value={(shared.programme_items || []).length} testid="con-count-prog" />
                    <KV label="Client Actions" value={(shared.client_actions || []).length} testid="con-count-ca" />
                    <KV label="Internal Tasks" value={(shared.internal_tasks || []).length} testid="con-count-tasks" />
                    <KV label="Contract Changes" value={(shared.contract_changes || []).length} testid="con-count-changes" />
                </div>
            </Card>
        </div>);
}


// =====================================================================
// 561-11 — Contract Administration
// =====================================================================

export function ContractAdminPage() {
    const { projectId, pkgId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const pkgLabel = data?.package?.package_name || "Package";
    usePageMeta({ title: `Contract admin · ${pkgLabel} | Studio G4 Admin` });
    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(
                `${API}/admin/projects/${projectId}/ws6/contract-admin/${pkgId}`,
                { withCredentials: true });
            setData(r.data);
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
    }, [projectId, pkgId]);
    useEffect(() => { refresh(); }, [refresh]);

    if (err) return <div data-testid="ca-error" className="text-red-700 text-[13px]">{err}</div>;
    if (!data) return <div data-testid="ca-loading" className="text-[13px] text-[#666]">Loading Contract Administration…</div>;
    const appt = data.lrs_ca_appointment_contract || {};
    const admins = data.administered_construction_contracts || [];
    const actions = data.formal_contract_actions || [];
    return (
        <div className="flex flex-col gap-4" data-testid="contract-admin-page">
            <header>
                <h1 className="font-manrope text-2xl font-semibold text-charcoal">
                    Contract Administration — {data.package.package_name}
                </h1>
                <p className="text-[13px] text-[#666]">Contract Administration appointment and the administered construction Contract Record(s) — kept distinct.</p>
            </header>
            <Card title="CA Service Appointment (fee basis)" testid="ca-appt">
                <div className="grid grid-cols-3 gap-4">
                    <KV label="Contract Basis" value={appt.contract_basis} testid="ca-appt-basis" />
                    <KV label="Executed" value={appt.execution_state === "executed" ? "Yes" : "No"} testid="ca-appt-executed" />
                    <KV label="Effective" value={appt.effective_state === "effective" ? "Yes" : "No"} testid="ca-appt-effective" />
                </div>
            </Card>
            <Card title={`Administered construction Contract${admins.length !== 1 ? "s" : ""}`}
                  testid="ca-admin-contracts">
                {admins.length === 0 && <p className="text-[12px] text-[#666]" data-testid="ca-no-admin">No administered construction contract linked.</p>}
                <ul className="flex flex-col gap-2">
                    {admins.map((c) => (
                        <li key={c.id} className="border border-[#E5E2DC] rounded p-2"
                            data-testid={`ca-admin-contract-${c.id}`}>
                            <div className="font-medium">{c.contract_title || c.id.slice(0, 8)}</div>
                            <div className="text-[11px] text-[#666]">
                                Basis: {c.contract_basis} · Sum: {currency(c.particulars?.contract_sum)}
                            </div>
                        </li>))}
                </ul>
            </Card>
            <Card title="Formal Contract Actions" testid="ca-actions"
                  actions={[
                      <span key="lock" className="text-[11px] text-[#666]" data-testid="ca-actions-note">
                          Draft → Preview → Confirm Issue → Issued.  Issued actions are immutable.
                      </span>]}>
                {actions.length === 0 && <p className="text-[12px] text-[#666]" data-testid="ca-no-actions">No formal Contract Actions yet.</p>}
                <ul className="flex flex-col gap-2">
                    {actions.map((a) => (
                        <li key={a.id} className="border border-[#E5E2DC] rounded p-2"
                            data-testid={`ca-action-${a.id}`}>
                            <div className="flex items-center justify-between">
                                <span className="font-medium">{a.reference} — {a.action_type}</span>
                                <Pill tone={a.status === "Issued" ? "good"
                                           : a.status === "Withdrawn" ? "bad"
                                           : "warn"}>
                                    {a.status}
                                </Pill>
                            </div>
                            <div className="text-[11px] text-[#666] mt-1">
                                Clause: {a.clause_or_mechanism || "—"} ·
                                Service method: {a.prescribed_service_method || "—"}
                                {a.issued_at ? ` · Issued ${a.issued_at.slice(0, 10)}` : null}
                            </div>
                        </li>))}
                </ul>
            </Card>
        </div>);
}


// =====================================================================
// 561-12 — Review Completion (Package + WS6)
// =====================================================================

export function ReviewCompletionPage() {
    const { projectId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    usePageMeta({ title: "Review completion · WS6 | Studio G4 Admin" });
    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(
                `${API}/admin/projects/${projectId}/ws6/completion`,
                { withCredentials: true });
            setData(r.data);
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
    }, [projectId]);
    useEffect(() => { refresh(); }, [refresh]);
    if (err) return <div data-testid="rc-error" className="text-red-700 text-[13px]">{err}</div>;
    if (!data) return <div data-testid="rc-loading" className="text-[13px] text-[#666]">Loading Review Completion…</div>;

    const ws6 = data.ws6_readiness || {};
    return (
        <div className="flex flex-col gap-4" data-testid="review-completion-page">
            <header>
                <h1 className="font-manrope text-2xl font-semibold text-charcoal">
                    Review Completion
                </h1>
                <p className="text-[13px] text-[#666]">Two-level completion — individual Service Package Completion (Level 1) and overall Workstage 6 Completion (Level 2).  Completing one package never completes another package, WS6, or the project.</p>
            </header>

            <Card title="Workstage 6 readiness" testid="rc-ws6-readiness">
                <div className="flex items-center gap-3">
                    <Pill tone={ws6.ready ? "good" : "warn"} testid="rc-ws6-pill">
                        {ws6.ready ? "Ready" : `${(ws6.blockers || []).length} blocker(s)`}
                    </Pill>
                    <span className="text-[12px] text-[#666]" data-testid="rc-next-hint">
                        {ws6.next_action_hint}
                    </span>
                </div>
                {(ws6.blockers || []).length > 0 && (
                    <ul className="mt-2 text-[11px]" data-testid="rc-ws6-blockers">
                        {(ws6.blockers || []).map((b, i) => (
                            <li key={i} data-testid={`rc-ws6-blocker-${i}`} className="text-[#7A2222]">
                                • {b.reason}
                            </li>))}
                    </ul>)}
                {ws6.ws7_routing && (
                    <div className="mt-2 text-[11px]" data-testid="rc-ws7-routing">
                        <strong>WS7 routing:</strong> Case {ws6.ws7_routing.case}
                        {ws6.ws7_routing.case === "A"
                            ? ` — ${ws6.ws7_routing.unresolved_blocking.length} unresolved WS7 requirement(s); WS7 will become Active on completion.`
                            : " — no WS7 required.  Project remains Active until deliberate Close Project."}
                    </div>)}
            </Card>

            <Card title="Package completion status" testid="rc-package-list">
                <ul className="flex flex-col gap-2">
                    {(data.packages || []).map((p) => (
                        <li key={p.package_id} className="border border-[#E5E2DC] rounded p-2"
                            data-testid={`rc-package-${p.package_id}`}>
                            <div className="flex items-center justify-between">
                                <span className="font-medium">{p.package_name}</span>
                                <div className="flex items-center gap-2">
                                    <Pill tone="neutral" testid={`rc-pkg-status-${p.package_id}`}>
                                        {p.status}
                                    </Pill>
                                    <Pill tone={p.readiness?.ready ? "good" : "warn"}
                                          testid={`rc-pkg-ready-${p.package_id}`}>
                                        {p.readiness?.ready ? "Ready" : "Blocked"}
                                    </Pill>
                                </div>
                            </div>
                            <div className="text-[11px] text-[#666] mt-1">
                                {(p.readiness?.blockers || []).length} blocker(s)
                            </div>
                        </li>))}
                </ul>
            </Card>
        </div>);
}
